import { Action } from '@anm/components/ActionsDropdown';
import { getDropdownAction, getDropdownActions, ActionsHandlers } from '@anm/helpers/dropdownActions';
import isMobileOrIPad from '@anm/helpers/is/isMobileOrIPad';
import { useSelector } from 'react-redux';

import { userSelectors } from '../../../../store/modules/user';
import useProjectsPermissions from '../../hooks/useProjectsPermissions';

import useAddToBrandAction from './useAddToBrandAction';
import useDownloadVideoAction from './useDownloadVideoAction';
import useDuplicateAction from './useDuplicateAction';
import useMoveProjectToWorkspaceAction from './useMoveProjectToWorkspaceAction';
import useStreamVideoAction from './useStreamVideoAction';

type ProjectDropdownActionsProps = Partial<ActionsHandlers> & {
  projectId: string;
  isPublished: boolean;
  canStreamVideo: boolean;
  canEditBrand: boolean;
  canEdit?: boolean;
  streamId?: string | null;
  onStreamEdit(): void;
  renderEditLink: (children: JSX.Element) => JSX.Element;
  renderLandingLink: (children: JSX.Element) => JSX.Element;
  renderSettingsLink: (children: JSX.Element) => JSX.Element;
  renderEmbedLink: (children: JSX.Element) => JSX.Element;
};

const usePlayerDropdownActions = ({
  streamId,
  onMove,
  onShare,
  onDelete,
  canEdit,
  projectId,
  onDownload,
  isPublished,
  onStreamEdit,
  onDuplicate,
  onAddToBrand,
  onStreamVideo,
  renderEditLink,
  canEditBrand,
  canStreamVideo,
  renderLandingLink,
  renderSettingsLink,
  renderEmbedLink
}: ProjectDropdownActionsProps) => {
  const { canCreateProject, canEditProject } = useProjectsPermissions();
  const editAction: Action = {
    ...getDropdownAction('onEditVideo'),
    ...(canEdit ? { renderLink: renderEditLink } : { handler: onStreamEdit })
  };

  const hasTeamAccess = useSelector(userSelectors.selectTeamAccess);
  const moveToWorkspaceAction = useMoveProjectToWorkspaceAction({ projectId });

  const duplicateAction = useDuplicateAction(onDuplicate);
  const streamVideoAction = useStreamVideoAction({ handler: onStreamVideo, projectId, canStreamVideo });
  const addToBrandAction = useAddToBrandAction({ handler: onAddToBrand, isPublished, projectId, canEditBrand });
  const downloadAction = useDownloadVideoAction({ handler: onDownload, projectId, canStreamVideo, streamId });

  const landingAction: Action = {
    ...getDropdownAction('onLanding'),
    renderLink: renderLandingLink
  };

  const settingsAction: Action = {
    ...getDropdownAction('onSettings'),
    renderLink: renderSettingsLink
  };
  const embedAction: Action = {
    ...getDropdownAction('onEmbed'),
    renderLink: renderEmbedLink
  };

  return [
    isMobileOrIPad() && landingAction,
    embedAction,
    !isMobileOrIPad() && canEditProject && editAction,
    streamVideoAction,
    addToBrandAction,
    !isMobileOrIPad() && settingsAction,
    hasTeamAccess && canEditProject && moveToWorkspaceAction,
    canCreateProject && duplicateAction,
    downloadAction,
    ...getDropdownActions({
      ...(onShare && { onShare }),
      ...(onMove && canEditProject && { onMove }),
      ...(onDelete && canEditProject && { onDelete })
    })
  ].filter(Boolean) as Action[];
};

export default usePlayerDropdownActions;
